import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { TotemLabel } from './TotemLabel';
import { Loader } from './Loader';
import { AuditLogCreatedSubscription, PatchOp, useGetAuditLogsQuery } from '../data/__generated__';
import { getExtendedFormattedDatetime } from '../helpers/dateTimes';
import { AUDIT_LOG_CREATED_SUBSCRIPTION } from 'data/subscriptions/auditLog';

type AuditLogsProps = {
    collectionDocumentId: string;
    label?: string;
};

function getCreatorName(createdBy: string, createdByName: string) {
    return createdByName || createdBy || 'Inconnu';
}

function formatPatchValue(value: unknown): string {
    if (value === null || value === undefined) {
        return 'null';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
}

function getOperationIcon(op: PatchOp): string {
    switch (op) {
        case PatchOp.Add:
            return '+';
        case PatchOp.Remove:
            return '−';
        case PatchOp.Replace:
            return '→';
        case PatchOp.Move:
            return '↔';
        case PatchOp.Copy:
            return '⧉';
        case PatchOp.Test:
            return '?';
        default:
            return '•';
    }
}

export const AuditLogs: React.FC<AuditLogsProps> = ({ collectionDocumentId, label }) => {
    const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
    const { data, loading, error, subscribeToMore } = useGetAuditLogsQuery({
        variables: { collectionDocumentId },
    });

    useEffect(() => {
        const unsubscribe = subscribeToMore<AuditLogCreatedSubscription>({
            variables: { collectionDocumentId },
            document: AUDIT_LOG_CREATED_SUBSCRIPTION,
            updateQuery: (prevData, { subscriptionData }) => ({
                auditLogs: [...prevData.auditLogs, subscriptionData.data.auditLogCreated],
            }),
        });

        return () => unsubscribe();
    }, [subscribeToMore]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <ErrorMessage>Erreur lors du chargement des logs d'audit: {error.message}</ErrorMessage>;
    }

    const auditLogs = data?.auditLogs || [];

    if (auditLogs.length === 0) {
        return (
            <Container>
                {label ? <TotemLabel>{label}</TotemLabel> : null}
                <NoDataMessage>Aucun historique de modification trouvé</NoDataMessage>
            </Container>
        );
    }

    // Trier par date de création décroissante (plus récent en premier)
    const sortedAuditLogs = [...auditLogs].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const toggleExpanded = (logId: string) => {
        setExpandedLogs((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(logId)) {
                newSet.delete(logId);
            } else {
                newSet.add(logId);
            }
            return newSet;
        });
    };

    return (
        <Container>
            {label ? <TotemLabel>{label}</TotemLabel> : null}
            <AuditLogsList>
                {sortedAuditLogs.map((auditLog) => {
                    const isExpanded = expandedLogs.has(auditLog._id);
                    const hasPatches = auditLog.patches.length > 0;

                    return (
                        <AuditLogItem key={auditLog._id}>
                            <AuditLogHeader onClick={() => hasPatches && toggleExpanded(auditLog._id)}>
                                <AuditLogInfo>
                                    <AuditLogDate>
                                        {getExtendedFormattedDatetime(new Date(auditLog.createdAt).getTime())} par{' '}
                                        {getCreatorName(auditLog.createdBy, auditLog.createdByName)}
                                    </AuditLogDate>
                                </AuditLogInfo>
                                {hasPatches && <ExpandIcon $isExpanded={isExpanded}>▼</ExpandIcon>}
                            </AuditLogHeader>

                            {hasPatches && isExpanded && (
                                <PatchesContainer>
                                    {auditLog.patches.map((patch, index) => {
                                        return (
                                            <PatchItem key={index}>
                                                <PatchHeader>
                                                    <PatchIcon>{getOperationIcon(patch.op)}</PatchIcon>
                                                    <PatchPath>{patch.path}</PatchPath>
                                                </PatchHeader>

                                                {patch.op === PatchOp.Replace &&
                                                    'oldValue' in patch &&
                                                    'value' in patch && (
                                                        <PatchValues>
                                                            <PatchValue>
                                                                <PatchValueLabel>Avant:</PatchValueLabel>
                                                                <PatchValueContent>
                                                                    {formatPatchValue(patch.oldValue)}
                                                                </PatchValueContent>
                                                            </PatchValue>
                                                            <PatchArrow>→</PatchArrow>
                                                            <PatchValue>
                                                                <PatchValueLabel>Après:</PatchValueLabel>
                                                                <PatchValueContent>
                                                                    {formatPatchValue(patch.value)}
                                                                </PatchValueContent>
                                                            </PatchValue>
                                                        </PatchValues>
                                                    )}

                                                {patch.op === PatchOp.Add && 'value' in patch && (
                                                    <PatchValue>
                                                        <PatchValueLabel>Valeur ajoutée:</PatchValueLabel>
                                                        <PatchValueContent>
                                                            {formatPatchValue(patch.value)}
                                                        </PatchValueContent>
                                                    </PatchValue>
                                                )}

                                                {patch.op === PatchOp.Remove && 'oldValue' in patch && (
                                                    <PatchValue>
                                                        <PatchValueLabel>Valeur supprimée:</PatchValueLabel>
                                                        <PatchValueContent>
                                                            {formatPatchValue(patch.oldValue)}
                                                        </PatchValueContent>
                                                    </PatchValue>
                                                )}

                                                {(patch.op === PatchOp.Move || patch.op === PatchOp.Copy) &&
                                                    'from' in patch && (
                                                        <PatchValue>
                                                            <PatchValueLabel>De:</PatchValueLabel>
                                                            <PatchValueContent>{patch.from}</PatchValueContent>
                                                        </PatchValue>
                                                    )}

                                                {patch.op === PatchOp.Test && 'value' in patch && (
                                                    <PatchValue>
                                                        <PatchValueLabel>Valeur testée:</PatchValueLabel>
                                                        <PatchValueContent>
                                                            {formatPatchValue(patch.value)}
                                                        </PatchValueContent>
                                                    </PatchValue>
                                                )}
                                            </PatchItem>
                                        );
                                    })}
                                </PatchesContainer>
                            )}
                        </AuditLogItem>
                    );
                })}
            </AuditLogsList>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const AuditLogsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const AuditLogItem = styled.div`
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 6px;
    padding: 6px 12px;
    background-color: ${({ theme }) => theme.backgroundColor};
    margin-bottom: 4px;
`;

const AuditLogHeader = styled.div<{ onClick?: () => void }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
`;

const AuditLogInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AuditLogDate = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
`;

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
    font-size: 12px;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.2s ease;
    user-select: none;
`;

const PatchesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const PatchItem = styled.div`
    padding: 4px 8px;
    border-radius: 4px;
    border-left: 3px solid ${({ theme }) => theme.ctaPrimaryColor};
`;

const PatchHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;

const PatchIcon = styled.span`
    font-weight: bold;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    font-size: 14px;
    min-width: 16px;
`;

const PatchPath = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
`;

const PatchValues = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const PatchArrow = styled.span`
    color: ${({ theme }) => theme.ctaPrimaryColor};
    font-weight: bold;
    font-size: 14px;
`;

const PatchValue = styled.div`
    flex: 1;
    min-width: 120px;
`;

const PatchValueLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    display: block;
    margin-bottom: 2px;
`;

const PatchValueContent = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
    background-color: ${({ theme }) => theme.backgroundColor};
    padding: 4px 6px;
    border-radius: 3px;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 80px;
    overflow-y: auto;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const NoDataMessage = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-style: italic;
    padding: 20px;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
    padding: 10px;
    background-color: ${({ theme }) => theme.backgroundColor};
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.errorColor};
`;
